// DashboardView.jsx — a HOME do Admin. View PURA: recebe { vm, actions }.
// Contrato: dashboard.vm.js (@version 4). Nada de fetch, formatação, cálculo de negócio ou
// reordenação: número, data, dinheiro, rótulo de status, texto de vazio e ORDEM vêm prontos.
//
// ═══════════════════════════════════════════════════════════════════════════════
// O DESENHO, EM TRÊS DECISÕES
//
// 1. TRÊS ZONAS, não uma grade. A tela lê de cima para baixo em ordem de urgência:
//      · FAIXA DE ALARME  — o que a Home levanta a voz para dizer (`critical`, ou `credits`/
//                           `plan` em `error`). Largura cheia, tom forte, ação de dinheiro.
//      · GRADE OPERACIONAL — agenda, ausências, páginas, faturamento, suporte, avisos.
//      · RODAPÉ DISCRETO   — o que tem `importance: 'low'` (cadastro, app, dica, vitrine):
//                            fundo de página, sem sombra, tiles pequenos. Presente, mas baixo.
//
// 2. GRADE DE 12 COLUNAS COM EMPACOTAMENTO. Cada `key` tem largura PREFERIDA e largura MÁXIMA
//    (`SPAN`); `packSpans` distribui as sobras na própria linha. Por isso a tela não abre buraco
//    quando a conta mostra 7 cards em vez de 12 — e em 1596px nenhuma linha fica pela metade.
//    Dentro dos cards de lista os itens também refluem (`auto-fill`), então card largo vira
//    lista de 2–3 colunas em vez de coluna magra num card gigante.
//
// 3. UM COMPONENTE POR `key` — 16 desenhos, não um card genérico. Agendamentos é um painel com
//    trilha de contadores + próximos; cadastro é um rodapé de tiles; faturamento é um gráfico;
//    a trilha é um anel de progresso com passos; o alarme é uma faixa. `status` viraliza em
//    tarja + chip + ícone (nunca só cor); `importance` vira ZONA e LARGURA.
//
// Regras seguidas: estilo só via `sx`; cor/raio/sombra/medida só via var(--*); MUI v6 + ícones
// @mui/icons-material; tipografia por <Typography variant>; imports só de react/@mui; toda ação
// passa por `actions.onAction(action)`; `onDismiss` só onde `dismissible`.
//
// Neutros saem de --bg-page/--divider/--text-* (papéis themeáveis) e nunca da escala --slate-*,
// que é global fixa e clara (quebraria os temas de superfície escura).
//
// Botão de dinheiro (comprar créditos, concluir pagamento, ver planos, registrar domínio) usa
// var(--color-cta) — cor de AÇÃO, deliberadamente fora da paleta de marca: em Salão, Tatuagem e
// Personal a primária é vinho/vermelha e vermelho em botão de compra lê "pare".
import { useState } from 'react';
import {
  Box, Typography, Button, IconButton, ButtonBase, Tooltip, Skeleton, LinearProgress,
  CircularProgress, Dialog, DialogContent,
} from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import DesignServicesRounded from '@mui/icons-material/DesignServicesRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded';
import LightbulbRounded from '@mui/icons-material/LightbulbRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import PaidRounded from '@mui/icons-material/PaidRounded';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRounded from '@mui/icons-material/TrendingFlatRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import SmsRounded from '@mui/icons-material/SmsRounded';
import SmsFailedRounded from '@mui/icons-material/SmsFailedRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import CircleRounded from '@mui/icons-material/CircleRounded';
import DoneRounded from '@mui/icons-material/DoneRounded';
import BeachAccessRounded from '@mui/icons-material/BeachAccessRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais — mesmos papéis de Serviços / Ausências / Suporte / Domínio
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)', sub: 'var(--color-primary-dark)', main: 'var(--color-primary)' },
};
const tint = (t) => TINT[t] || TINT.neutral;

// Cor de AÇÃO comercial (interesse do clique é nosso). Fallback no verde escuro que já existe,
// que dá ~7:1 com --on-primary. Nunca --color-success como preenchimento (3,45:1, reprova).
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';

const NUM = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' };
// Peso de título na fonte de CORPO. A fonte do nicho saiu daqui: título de card, valor e número
// ficam na fonte de corpo (hierarquia por peso e tamanho) — só o título de página (h1) leva a fonte
// do nicho. Antes, em Barbearia, isto colocava display condensada em "R$ 8.430,00".
const HEAD = { fontWeight: 'var(--fw-bold)' };
// TÍTULO DE PÁGINA: <Typography variant="h1"> puro. Tamanho, peso, altura de linha e a redução
// no celular vivem no tema pinado (muiThemeConfig) — e a fonte do nicho entra aí, só aí.
const ONE_LINE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 44,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: '2px' },
};

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 44,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  boxShadow: 'var(--shadow-card)',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: '2px' },
};

const OUTLINE_BTN_SX = {
  minHeight: 44, px: 2, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--divider)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', textTransform: 'none',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
};

const LINK_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
};

const QUIET_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
};

/** Entrada escalonada: o olho recebe a tela na ordem da leitura, não tudo de uma vez. */
const rise = (delay = 0) => ({
  '@keyframes homeRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'homeRise .5s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const PULSE = {
  '@keyframes homePulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.12)', opacity: 0.85 },
  },
  animation: 'homePulse 2.4s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

/* ═══════════════════════════════════════════════════════════════════════════
   Peças compartilhadas (vocabulário visual das telas irmãs)
   ═══════════════════════════════════════════════════════════════════════════ */

/** Chip de estado — DOIS REGISTROS, um critério: o estado PEDE AÇÃO ou não.
 *   · error / warning  → pastilha preenchida (tint + fio + ícone): salta numa tabela de 40 linhas;
 *   · success / info / neutral → PONTO + rótulo, sem pastilha: a coluna fica calma e quem aparece
 *     é o dado. Numa lista longa, 40 pastilhas verdes competem com o conteúdo e escondem as duas
 *     vermelhas — que são as únicas que mudam o que o dono do negócio vai fazer agora.
 *  Estado nunca só por cor: o rótulo nomeia o estado nos dois registros (e a pastilha leva ícone).
 *  Vale igual nos dois modos: o ponto usa o -fg do tint, que é medido em claro e em escuro. */
const CHIP_FILLED = { error: true, warning: true };

function ToneChip({ tone = 'neutral', icon, label, strong = false }) {
  const t = tint(tone);
  if (!CHIP_FILLED[tone]) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', flexShrink: 0, minWidth: 0 }}>
      <Box aria-hidden="true" component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: tone === 'neutral' ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: strong ? 'var(--fw-bold)' : 'var(--fw-medium)', ...ONE_LINE }}>
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%',
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: 1, py: '3px', flexShrink: 0,
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: strong ? 'var(--fw-bold)' : 'var(--fw-medium)', ...ONE_LINE }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Número + rótulo. Existe para nunca precisarmos escrever plural que o contrato não manda. */
function MiniStat({ value, label, tone = 'neutral', big = false }) {
  const t = tint(tone);
  const strong = tone !== 'neutral';
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant={big ? 'h5' : 'h6'}
        component="p"
        sx={{ ...HEAD, ...NUM, color: strong ? t.fg : 'var(--text-primary)', lineHeight: 1.1 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: strong ? t.sub : 'var(--text-secondary)', lineHeight: 1.35 }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Nota dentro de um card (aviso, mensagem pronta do VM). */
function NoteRow({ tone = 'info', icon, children, sx }) {
  const t = tint(tone);
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, ...sx,
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, flexShrink: 0, mt: '1px' }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

/** Linha de lista: vira botão quando tem destino, e continua legível quando não tem. */
function Row({ onClick, label, children, sx, accent }) {
  const common = {
    display: 'flex', width: '100%', alignItems: 'center', gap: 1.25,
    px: 1.25, py: 1, minHeight: 52, textAlign: 'left', minWidth: 0,
    borderRadius: 'var(--radius-sm)',
    borderLeft: '3px solid', borderLeftColor: accent || 'transparent',
    ...sx,
  };
  if (!onClick) return <Box sx={common}>{children}</Box>;
  return (
    <ButtonBase
      focusRipple
      aria-label={label}
      onClick={onClick}
      sx={{
        ...common, justifyContent: 'flex-start',
        transition: 'background-color .15s, transform .15s',
        '&:hover': { bgcolor: 'var(--bg-page)' },
        '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
      }}
    >
      {children}
    </ButtonBase>
  );
}

/** Vazio de um card: texto SEMPRE o `emptyLabel` escrito pelo produto. */
function EmptyNote({ icon, label }) {
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        gap: 1, py: 2.5, px: 1.5, borderRadius: 'var(--radius-sm)',
        border: '1px dashed', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)',
      }}
    >
      <Box sx={{ display: 'flex', color: 'var(--text-secondary)', opacity: 0.7 }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45, textWrap: 'pretty' }}>
        {label}
      </Typography>
    </Box>
  );
}

function DismissButton({ card, onDismiss }) {
  if (!card.dismissible) return null;
  return (
    <Tooltip title="Dispensar">
      <IconButton
        aria-label={`Dispensar ${card.title}`}
        onClick={() => onDismiss?.(card.key)}
        sx={{
          width: 40, height: 40, flexShrink: 0, ml: 'auto',
          color: 'var(--text-secondary)', opacity: 0.6,
          transition: 'opacity .15s, background-color .15s',
          '&:hover': { opacity: 1, bgcolor: 'var(--bg-page)', color: 'var(--text-primary)' },
          '&:focus-visible': { opacity: 1, outline: '2px solid var(--color-primary)', outlineOffset: '-2px' },
        }}
      >
        <CloseRounded sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
}

const ACTION_ICON = {
  external: <OpenInNewRounded sx={{ fontSize: 16 }} />,
  navigate: <ArrowForwardRounded sx={{ fontSize: 16 }} />,
  emit: <ChevronRightRounded sx={{ fontSize: 18 }} />,
};

/** Rodapé de ações do card. A primeira é o caminho principal; as outras ficam discretas. */
function CardActions({ actions, onAction, tone = 'link' }) {
  const list = actions || [];
  if (!list.length) return null;
  return (
    <Box
      sx={{
        mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'var(--divider)',
        display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end',
      }}
    >
      {list.map((action, i) => (
        <Button
          key={`${action.label}-${i}`}
          onClick={() => onAction(action)}
          endIcon={ACTION_ICON[action.type]}
          sx={i === 0 && tone === 'link' ? LINK_BTN_SX : QUIET_BTN_SX}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
}

/** Casca comum: superfície, tarja de estado e hover. O INTERIOR é próprio de cada `key`. */
function CardShell({ tone = 'neutral', accent = false, delay = 0, children, sx }) {
  const t = tint(tone);
  return (
    <Box
      component="section"
      sx={{
        ...CARD_SX, position: 'relative', overflow: 'hidden', minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 1.5,
        p: { xs: 1.75, sm: 2.25 }, pl: accent ? { xs: 2, sm: 2.5 } : undefined,
        transition: 'box-shadow .18s, border-color .18s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: accent ? t.main : 'var(--color-primary-light)' },
        ...rise(delay), ...sx,
      }}
    >
      {accent && (
        <Box aria-hidden="true" sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: t.main }} />
      )}
      {children}
    </Box>
  );
}

/** Cabeçalho do card: disco de ícone + título + subtítulo + área direita + dispensar. */
function CardHead({ icon, tone = 'brand', title, subtitle, right, card, onDismiss }) {
  const t = tint(tone);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
          bgcolor: t.bg, border: '1px solid', borderColor: t.border, color: t.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.25, textWrap: 'pretty' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.4, mt: '2px' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
      {card && <DismissButton card={card} onDismiss={onDismiss} />}
    </Box>
  );
}

/** Lista que reflui: em card largo vira 2–3 colunas em vez de coluna magra. */
const FLUID_LIST_SX = {
  display: 'grid', gap: 0.25, minWidth: 0, flex: 1,
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
};

/** Barra de proporção (plano, faturamento). Percentual é POSIÇÃO — não se anima como tempo. */
function Meter({ percent, tone = 'neutral', label }) {
  const t = tint(tone);
  const pct = Math.max(0, Math.min(100, percent || 0));
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{ height: 8, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', overflow: 'hidden' }}
    >
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: t.main, borderRadius: 999, transition: 'width .3s ease' }} />
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   1 · appointments — O HERÓI
   Quatro naturezas no mesmo resumo: carga de hoje, carga da semana, o que pede ação e os
   próximos. Vira um painel de duas partes: trilha de contadores à esquerda (a pendência
   destacada quando existe) e a fila dos próximos à direita, com "hoje" marcado por tarja.
   ═══════════════════════════════════════════════════════════════════════════ */

function CounterTile({ counter, onAction, emphasis = 'neutral' }) {
  if (!counter) return null;
  const strong = counter.status && counter.status !== 'neutral';
  const t = tint(strong ? counter.status : emphasis === 'brand' ? 'brand' : 'neutral');
  const body = (
    <>
      <Typography
        variant="h4"
        component="p"
        sx={{ ...HEAD, ...NUM, color: strong || emphasis === 'brand' ? t.fg : 'var(--text-primary)', lineHeight: 1 }}
      >
        {counter.value}
      </Typography>
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {strong && <WarningAmberRounded sx={{ fontSize: 16, color: t.fg, flexShrink: 0 }} />}
        <Typography
          variant="body2"
          sx={{ color: strong ? t.sub : 'var(--text-secondary)', fontWeight: strong ? 'var(--fw-bold)' : 'var(--fw-medium)', lineHeight: 1.3, textWrap: 'pretty' }}
        >
          {counter.label}
        </Typography>
      </Box>
    </>
  );
  const sx = {
    display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0,
    px: 1.5, py: 1.25, minHeight: 64, textAlign: 'left', justifyContent: 'flex-start',
    borderRadius: 'var(--radius-sm)', border: '1px solid',
    borderColor: strong ? t.border : emphasis === 'brand' ? 'var(--color-primary-soft)' : 'var(--divider)',
    bgcolor: strong || emphasis === 'brand' ? t.bg : 'var(--surface)',
  };
  if (!counter.action) return <Box sx={sx}>{body}</Box>;
  return (
    <ButtonBase
      focusRipple
      aria-label={`${counter.value} — ${counter.label}`}
      onClick={() => onAction(counter.action)}
      sx={{
        ...sx, transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: strong ? t.main : 'var(--color-primary)', boxShadow: 'var(--shadow-card)' },
        '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
      }}
    >
      {body}
    </ButtonBase>
  );
}

const APPT_STATUS_ICON = {
  success: <CheckCircleRounded sx={{ fontSize: 14 }} />,
  warning: <HourglassTopRounded sx={{ fontSize: 14 }} />,
  error: <CloseRounded sx={{ fontSize: 14 }} />,
  neutral: <ScheduleRounded sx={{ fontSize: 14 }} />,
};

function AppointmentsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const upcoming = d.upcoming || [];
  const rest = Math.max(0, (d.totalUpcoming || 0) - upcoming.length);
  const pending = d.pendingConfirmation;
  const alert = card.status === 'warning' || card.status === 'error';

  return (
    <CardShell tone={card.status} accent={alert} delay={delay}>
      <CardHead
        icon={<EventAvailableRounded sx={{ fontSize: 22 }} />}
        tone={alert ? card.status : 'brand'}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
        right={pending && pending.status === 'warning' ? (
          <ToneChip
            tone="warning"
            strong
            icon={<HourglassTopRounded sx={{ fontSize: 14, ...PULSE }} />}
            label={`${pending.value} aguardando`}
          />
        ) : null}
      />

      {/* Em `lg` o card mede ~570px (8 de 12 em 876px de conteúdo): ali a trilha vira uma FILA
          horizontal e a fila dos próximos ganha a largura toda. Em `sm`/`md` (card cheio) e em
          `xl` (card largo) a trilha volta para a coluna da esquerda. */}
      <Box
        sx={{
          display: 'grid', gap: { xs: 1.5, sm: 2 }, alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(200px, 264px) 1fr', lg: '1fr', xl: 'minmax(220px, 280px) 1fr' },
        }}
      >
        {/* trilha de contadores */}
        <Box
          sx={{
            display: 'flex', gap: 0.75, minWidth: 0,
            flexDirection: { xs: 'column', sm: 'column', lg: 'row', xl: 'column' },
            flexWrap: { lg: 'wrap', xl: 'nowrap' },
            '& > *': { flex: { lg: '1 1 200px', xl: '0 0 auto' } },
          }}
        >
          <CounterTile counter={d.today} onAction={onAction} emphasis="brand" />
          <CounterTile counter={d.next7Days} onAction={onAction} />
          <CounterTile counter={pending} onAction={onAction} />
        </Box>

        {/* a fila dos próximos */}
        <Box sx={{ minWidth: 0 }}>
          {upcoming.length === 0 ? (
            <EmptyNote icon={<EventAvailableRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
              {upcoming.map((it) => {
                return (
                  <Row
                    key={it.id}
                    onClick={it.action ? () => onAction(it.action) : undefined}
                    label={`${it.customer} — ${it.whenLabel} — ${it.statusLabel}`}
                    accent={it.isToday ? 'var(--color-primary)' : undefined}
                    sx={{
                      alignItems: 'flex-start',
                      bgcolor: it.isToday ? 'var(--color-primary-soft)' : 'transparent',
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        title={it.customer}
                        sx={{ ...ONE_LINE, fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}
                      >
                        {it.customer}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mt: '2px', minWidth: 0 }}>
                        <CircleRounded sx={{ fontSize: 6, color: it.isToday ? 'var(--color-primary)' : 'var(--text-secondary)', flexShrink: 0 }} />
                        <Typography
                          variant="caption"
                          sx={{ ...ONE_LINE, color: it.isToday ? 'var(--color-primary-dark)' : 'var(--text-secondary)', fontWeight: it.isToday ? 'var(--fw-bold)' : 'var(--fw-regular)' }}
                        >
                          {it.whenLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ ...ONE_LINE, color: 'var(--text-secondary)' }}>
                          · {it.professional}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      <ToneChip tone={it.status} icon={APPT_STATUS_ICON[it.status] || APPT_STATUS_ICON.neutral} label={it.statusLabel} />
                    </Box>
                  </Row>
                );
              })}
              {rest > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', pl: 1.5, pt: 0.75, ...NUM }}>
                  e mais {rest} no período
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2 · absences — quem está fora AGORA vs. quem vai ficar
   ═══════════════════════════════════════════════════════════════════════════ */

function AbsencesCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const items = d.items || [];
  const alert = (d.activeCount || 0) > 0;

  return (
    <CardShell tone={card.status} accent={alert} delay={delay}>
      <CardHead
        icon={<EventBusyRounded sx={{ fontSize: 22 }} />}
        tone={alert ? 'warning' : 'brand'}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <MiniStat value={d.activeCount ?? 0} label="fora agora" tone={alert ? 'warning' : 'neutral'} big />
        <Box aria-hidden="true" sx={{ width: '1px', bgcolor: 'var(--divider)', alignSelf: 'stretch' }} />
        <MiniStat value={d.scheduledCount ?? 0} label="programadas" big />
      </Box>

      {items.length === 0 ? (
        <EmptyNote icon={<BeachAccessRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <Box sx={FLUID_LIST_SX}>
          {items.map((it) => (
            <Row key={it.id} accent={it.isActive ? 'var(--color-warning)' : undefined} sx={{ alignItems: 'flex-start', bgcolor: it.isActive ? 'var(--tint-warning-bg)' : 'transparent' }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" title={it.professional} sx={{ ...ONE_LINE, fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
                  {it.professional}
                </Typography>
                <Typography variant="caption" sx={{ color: it.isActive ? 'var(--tint-warning-sub)' : 'var(--text-secondary)', mt: '2px' }}>
                  {it.period}
                  {it.reason ? ` · ${it.reason}` : ''}
                </Typography>
              </Box>
              {it.isActive && (
                <ToneChip tone="warning" icon={<BeachAccessRounded sx={{ fontSize: 14 }} />} label="Fora agora" />
              )}
            </Row>
          ))}
        </Box>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3 · schedules — as agendas que recebem agendamento público
   ═══════════════════════════════════════════════════════════════════════════ */

function SchedulesCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const items = d.items || [];

  return (
    <CardShell tone={card.status} accent={card.status === 'warning'} delay={delay}>
      <CardHead
        icon={<CalendarMonthRounded sx={{ fontSize: 22 }} />}
        tone={card.status === 'warning' ? 'warning' : 'brand'}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
        right={<MiniStat value={d.total ?? 0} label="no ar" />}
      />

      {items.length === 0 ? (
        <EmptyNote icon={<CalendarMonthRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <Box sx={FLUID_LIST_SX}>
          {items.map((it) => {
            const meta = [it.professional, it.place].filter(Boolean).join(' · ');
            return (
              <Row
                key={it.id}
                onClick={it.action ? () => onAction(it.action) : undefined}
                label={it.action ? `${it.action.label}: ${it.name}` : undefined}
                sx={{ alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" title={it.name} sx={{ ...ONE_LINE, fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
                    {it.name}
                  </Typography>
                  {meta && (
                    <Typography variant="caption" title={meta} sx={{ ...ONE_LINE, color: 'var(--text-secondary)', mt: '2px' }}>
                      {meta}
                    </Typography>
                  )}
                </Box>
                {it.shareUrl && (
                  <OpenInNewRounded sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} />
                )}
              </Row>
            );
          })}
        </Box>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   4 · pages — publicada e rascunho são situações DIFERENTES
   ═══════════════════════════════════════════════════════════════════════════ */

function PagesCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const items = d.items || [];
  const onlyDrafts = (d.publishedCount || 0) === 0 && (d.draftCount || 0) > 0;

  return (
    <CardShell tone={card.status} accent={card.status === 'warning'} delay={delay}>
      <CardHead
        icon={<ArticleRounded sx={{ fontSize: 22 }} />}
        tone={card.status === 'warning' ? 'warning' : 'brand'}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <MiniStat value={d.publishedCount ?? 0} label="no ar" tone={(d.publishedCount || 0) > 0 ? 'success' : 'neutral'} big />
        <Box aria-hidden="true" sx={{ width: '1px', bgcolor: 'var(--divider)', alignSelf: 'stretch' }} />
        <MiniStat value={d.draftCount ?? 0} label="em rascunho" tone={onlyDrafts ? 'warning' : 'neutral'} big />
      </Box>

      {onlyDrafts && (
        <NoteRow tone="warning" icon={<WarningAmberRounded sx={{ fontSize: 18 }} />}>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            Rascunho não fica visível para ninguém. Publique para o seu link começar a receber visita.
          </Typography>
        </NoteRow>
      )}

      {items.length === 0 ? (
        <EmptyNote icon={<LanguageRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <Box sx={FLUID_LIST_SX}>
          {items.map((it) => (
            <Row
              key={it.id}
              onClick={it.action ? () => onAction(it.action) : undefined}
              label={it.action ? `${it.action.label}: ${it.title}` : undefined}
              accent={it.published ? undefined : 'var(--color-warning)'}
              sx={{ alignItems: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" title={it.title} sx={{ ...ONE_LINE, fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
                  {it.title}
                </Typography>
                <Typography variant="caption" title={it.slug} sx={{ ...ONE_LINE, color: 'var(--text-secondary)', mt: '2px' }}>
                  /{it.slug}
                </Typography>
              </Box>
              <ToneChip
                tone={it.published ? 'success' : 'warning'}
                icon={it.published ? <CheckCircleRounded sx={{ fontSize: 14 }} /> : <ArticleRounded sx={{ fontSize: 14 }} />}
                label={it.statusLabel}
              />
            </Row>
          ))}
        </Box>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5 · registry — o RODAPÉ do cadastro. Seis destinos, peso baixo, densidade alta.
   Container quieto (fundo de página, sem sombra) para não competir com o operacional.
   ═══════════════════════════════════════════════════════════════════════════ */

const REGISTRY_ORDER = ['professionals', 'services', 'persons', 'places', 'schedules', 'appointments'];
const REGISTRY_ICON = {
  professionals: BadgeRounded,
  services: DesignServicesRounded,
  persons: GroupsRounded,
  places: StorefrontRounded,
  schedules: CalendarMonthRounded,
  appointments: EventAvailableRounded,
};

function RegistryStrip({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const tiles = REGISTRY_ORDER.map((k) => ({ k, c: d[k] })).filter((x) => x.c);

  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)',
        p: { xs: 1.25, sm: 1.75 }, display: 'flex', flexDirection: 'column', gap: 1.25,
        ...rise(delay),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="overline" component="h2" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
          {card.title}
        </Typography>
        <Box aria-hidden="true" sx={{ flex: 1, height: '1px', bgcolor: 'var(--divider)' }} />
        <DismissButton card={card} onDismiss={onDismiss} />
      </Box>

      <Box
        sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
        }}
      >
        {tiles.map(({ k, c }) => {
          const Icon = REGISTRY_ICON[k];
          const body = (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--text-secondary)' }}>
                <Icon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ ...ONE_LINE, color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
                  {c.label}
                </Typography>
              </Box>
              <Typography variant="h5" component="p" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.1, mt: 0.5 }}>
                {c.value}
              </Typography>
            </>
          );
          const sx = {
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%',
            minWidth: 0, p: 1.25, minHeight: 72, textAlign: 'left',
            borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)',
            border: '1px solid', borderColor: 'var(--divider)',
          };
          if (!c.action) return <Box key={k} sx={sx}>{body}</Box>;
          return (
            <ButtonBase
              key={k}
              focusRipple
              aria-label={`${c.label}: ${c.value}`}
              onClick={() => onAction(c.action)}
              sx={{
                ...sx, justifyContent: 'flex-start', transition: 'border-color .15s, box-shadow .15s, transform .15s',
                '&:hover': { borderColor: 'var(--color-primary)', boxShadow: 'var(--shadow-card)', transform: 'translateY(-1px)' },
                '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
                '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
              }}
            >
              {body}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   6 · firstSteps — a TRILHA. Anel de progresso + o próximo passo em evidência.
   Estado do passo nunca só por cor: ícone + rótulo ("Feito", "Próximo passo").
   ═══════════════════════════════════════════════════════════════════════════ */

function ProgressRing({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{ position: 'relative', width: 104, height: 104, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={104}
        thickness={4}
        aria-hidden="true"
        sx={{ position: 'absolute', color: 'var(--divider)' }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={104}
        thickness={4}
        aria-label={`${done} de ${total} passos concluídos`}
        sx={{ position: 'absolute', color: 'var(--color-primary)', '& circle': { strokeLinecap: 'round' } }}
      />
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" component="p" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1 }}>
          {done}<Box component="span" sx={{ color: 'var(--text-secondary)' }}>/{total}</Box>
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>passos</Typography>
      </Box>
    </Box>
  );
}

function FirstStepsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const steps = d.steps || [];

  return (
    <CardShell tone="info" accent delay={delay} sx={{ gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 2, sm: 3 } }}>
        <ProgressRing done={d.doneCount || 0} total={d.totalCount || steps.length || 0} />
        <Box sx={{ minWidth: 220, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketLaunchRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
            <Typography variant="overline" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
              Primeiros passos
            </Typography>
          </Box>
          <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', mt: 0.5, textWrap: 'pretty' }}>
            {card.title}
          </Typography>
          {card.subtitle && (
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.75, lineHeight: 1.5, maxWidth: 520, textWrap: 'pretty' }}>
              {card.subtitle}
            </Typography>
          )}
          {(card.actions || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              <Button
                onClick={() => onAction(card.actions[0])}
                endIcon={<ArrowForwardRounded />}
                disableElevation
                sx={PRIMARY_BTN_SX}
              >
                {card.actions[0].label}
              </Button>
              {card.actions.slice(1).map((action, i) => (
                <Button key={`${action.label}-${i}`} onClick={() => onAction(action)} sx={QUIET_BTN_SX}>
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
        <DismissButton card={card} onDismiss={onDismiss} />
      </Box>

      <Box
        sx={{
          display: 'grid', gap: 1,
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
        }}
      >
        {steps.map((s, i) => {
          const isNext = !s.done && s.key === d.nextStepKey;
          const stateLabel = s.done ? 'Feito' : isNext ? 'Próximo passo' : 'Pendente';
          const body = (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 26, height: 26, flexShrink: 0, borderRadius: '50%',
                    display: 'grid', placeItems: 'center',
                    bgcolor: s.done ? 'var(--tint-success-bg)' : isNext ? 'var(--color-primary)' : 'var(--bg-page)',
                    border: '1px solid',
                    borderColor: s.done ? 'var(--tint-success-border)' : isNext ? 'var(--color-primary)' : 'var(--divider)',
                    color: s.done ? 'var(--tint-success-fg)' : isNext ? 'var(--on-primary)' : 'var(--text-secondary)',
                  }}
                >
                  {s.done
                    ? <DoneRounded sx={{ fontSize: 16 }} />
                    : <Typography variant="caption" sx={{ ...NUM, fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{i + 1}</Typography>}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ minWidth: 0, fontWeight: 'var(--fw-bold)', color: s.done ? 'var(--text-secondary)' : 'var(--text-primary)', lineHeight: 1.3, textWrap: 'pretty' }}
                >
                  {s.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, pl: '34px', width: '100%' }}>
                {s.done
                  ? <CheckCircleRounded sx={{ fontSize: 14, color: 'var(--tint-success-fg)' }} />
                  : <RadioButtonUncheckedRounded sx={{ fontSize: 14, color: isNext ? 'var(--color-primary-dark)' : 'var(--text-secondary)' }} />}
                <Typography
                  variant="caption"
                  sx={{ color: s.done ? 'var(--tint-success-fg)' : isNext ? 'var(--color-primary-dark)' : 'var(--text-secondary)', fontWeight: isNext ? 'var(--fw-bold)' : 'var(--fw-medium)' }}
                >
                  {stateLabel}
                </Typography>
                {!s.done && s.action && (
                  <ChevronRightRounded sx={{ fontSize: 16, color: isNext ? 'var(--color-primary-dark)' : 'var(--text-secondary)', ml: 'auto' }} />
                )}
              </Box>
            </>
          );
          const sx = {
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%',
            minWidth: 0, p: 1.25, minHeight: 82, textAlign: 'left',
            borderRadius: 'var(--radius-sm)',
            bgcolor: isNext ? 'var(--color-primary-soft)' : 'var(--surface)',
            border: isNext ? '2px solid' : '1px solid',
            borderColor: isNext ? 'var(--color-primary)' : 'var(--divider)',
          };
          if (!s.action) return <Box key={s.key} sx={sx}>{body}</Box>;
          return (
            <ButtonBase
              key={s.key}
              focusRipple
              aria-label={`${s.label} — ${stateLabel}`}
              onClick={() => onAction(s.action)}
              sx={{
                ...sx, justifyContent: 'flex-start', transition: 'border-color .15s, box-shadow .15s',
                '&:hover': { borderColor: 'var(--color-primary)', boxShadow: 'var(--shadow-card)' },
                '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
              }}
            >
              {body}
            </ButtonBase>
          );
        })}
      </Box>
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   7 · finance — quatro meses de histórico dão EVOLUÇÃO, não um número solto
   ═══════════════════════════════════════════════════════════════════════════ */

const TREND_ICON = {
  success: <TrendingUpRounded sx={{ fontSize: 14 }} />,
  warning: <TrendingDownRounded sx={{ fontSize: 14 }} />,
  neutral: <TrendingFlatRounded sx={{ fontSize: 14 }} />,
};

function FinanceCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const periods = d.periods || [];
  const max = periods.reduce((m, p) => Math.max(m, p.gross || 0), 0);

  return (
    <CardShell tone={card.status} delay={delay}>
      <CardHead
        icon={<PaidRounded sx={{ fontSize: 22 }} />}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      {periods.length === 0 && !d.grossLabel ? (
        <EmptyNote icon={<PaidRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <>
          {/* o valor manda sozinho na linha: em card estreito ele nunca quebra no meio do número */}
          <Typography variant="h4" component="p" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1, whiteSpace: 'nowrap' }}>
            {d.grossLabel}
          </Typography>
          {d.trendLabel && (
            <Box>
              <ToneChip
                tone={d.trendStatus === 'neutral' ? 'neutral' : d.trendStatus}
                icon={TREND_ICON[d.trendStatus] || TREND_ICON.neutral}
                label={d.trendLabel}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 2 } }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>
              Líquido <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>{d.netLabel}</Box>
            </Typography>
            {d.previousGrossLabel && (
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>
                Mês anterior <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{d.previousGrossLabel}</Box>
              </Typography>
            )}
          </Box>

          {/* evolução: proporção calculada de `gross`; o rótulo mostrado é sempre `grossLabel` */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 108, mt: 0.5 }}>
            {periods.map((p, i) => {
              const last = i === periods.length - 1;
              const h = max > 0 ? Math.max(8, Math.round(((p.gross || 0) / max) * 100)) : 8;
              return (
                <Tooltip key={p.label} title={`${p.label}: ${p.grossLabel}`}>
                  <Box
                    sx={{
                      flex: 1, minWidth: 0, height: '100%',
                      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ ...ONE_LINE, ...NUM, maxWidth: '100%', color: last ? 'var(--color-primary-dark)' : 'var(--text-secondary)', fontWeight: last ? 'var(--fw-bold)' : 'var(--fw-regular)', display: { xs: 'none', sm: 'block' } }}
                    >
                      {p.grossLabel}
                    </Typography>
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: '100%', maxWidth: 46, height: `${h}%`,
                        borderRadius: 'var(--radius-sm)',
                        bgcolor: last ? 'var(--color-primary)' : 'var(--color-primary-soft)',
                        border: '1px solid', borderColor: last ? 'var(--color-primary)' : 'var(--color-primary-light)',
                        transformOrigin: 'bottom',
                        '@keyframes homeBar': { from: { transform: 'scaleY(0)' }, to: { transform: 'scaleY(1)' } },
                        animation: 'homeBar .5s cubic-bezier(.2,.7,.3,1) both',
                        animationDelay: `${120 + i * 70}ms`,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                      }}
                    />
                    <Typography variant="caption" sx={{ ...ONE_LINE, maxWidth: '100%', color: 'var(--text-secondary)' }}>
                      {p.label}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   8 · supportTickets — o que importa é "responderam e você não leu"
   ═══════════════════════════════════════════════════════════════════════════ */

const TICKET_ICON = {
  warning: <SupportAgentRounded sx={{ fontSize: 14 }} />,
  info: <HourglassTopRounded sx={{ fontSize: 14 }} />,
  success: <CheckCircleRounded sx={{ fontSize: 14 }} />,
};

function SupportTicketsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const items = d.items || [];
  const waiting = (d.awaitingYouCount || 0) > 0;

  return (
    <CardShell tone={card.status} accent={waiting} delay={delay}>
      <CardHead
        icon={<SupportAgentRounded sx={{ fontSize: 22 }} />}
        tone={waiting ? 'warning' : 'info'}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <MiniStat value={d.openCount ?? 0} label="em aberto" big />
        <Box aria-hidden="true" sx={{ width: '1px', bgcolor: 'var(--divider)', alignSelf: 'stretch' }} />
        <MiniStat value={d.awaitingYouCount ?? 0} label="aguardando você" tone={waiting ? 'warning' : 'neutral'} big />
      </Box>

      {items.length === 0 ? (
        <EmptyNote icon={<SupportAgentRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <Box sx={FLUID_LIST_SX}>
          {items.map((it) => {
            const unread = (it.unreadCount || 0) > 0;
            return (
              <Row
                key={it.id}
                onClick={it.action ? () => onAction(it.action) : undefined}
                label={it.action ? `${it.action.label}: ${it.subject}` : undefined}
                accent={unread ? 'var(--color-warning)' : undefined}
                sx={{ alignItems: 'flex-start', bgcolor: unread ? 'var(--tint-warning-bg)' : 'transparent' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.35,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}
                  >
                    {it.subject}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                    <ToneChip tone={it.status} icon={TICKET_ICON[it.status] || TICKET_ICON.info} label={it.statusLabel} />
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{it.lastActivityLabel}</Typography>
                  </Box>
                </Box>
                {unread && (
                  <Box
                    aria-label={`${it.unreadCount} sem leitura`}
                    sx={{
                      flexShrink: 0, minWidth: 24, height: 24, px: 0.75, borderRadius: 999,
                      bgcolor: 'var(--color-warning)', color: 'var(--on-primary)',
                      display: 'grid', placeItems: 'center', ...PULSE,
                    }}
                  >
                    <Typography variant="caption" sx={{ ...NUM, fontWeight: 'var(--fw-bold)', color: 'inherit' }}>
                      {it.unreadCount}
                    </Typography>
                  </Box>
                )}
              </Row>
            );
          })}
        </Box>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   9 · notifications — os últimos avisos, em linha do tempo curta
   ═══════════════════════════════════════════════════════════════════════════ */

function NotificationsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const items = d.items || [];
  const unread = d.unreadCount || 0;

  return (
    <CardShell tone={card.status} delay={delay}>
      <CardHead
        icon={<NotificationsActiveRounded sx={{ fontSize: 22 }} />}
        tone="info"
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
        right={unread > 0 ? (
          <Box
            aria-label={`${unread} sem leitura`}
            sx={{
              flexShrink: 0, minWidth: 24, height: 24, px: 0.75, borderRadius: 999,
              bgcolor: 'var(--color-info)', color: 'var(--on-primary)', display: 'grid', placeItems: 'center',
            }}
          >
            <Typography variant="caption" sx={{ ...NUM, fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{unread}</Typography>
          </Box>
        ) : null}
      />

      {items.length === 0 ? (
        <EmptyNote icon={<NotificationsActiveRounded sx={{ fontSize: 28 }} />} label={d.emptyLabel} />
      ) : (
        <Box sx={FLUID_LIST_SX}>
          {items.map((it) => (
            <Row key={it.id} sx={{ alignItems: 'flex-start', py: 1.25 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, mt: '6px',
                  bgcolor: it.isRead ? 'var(--divider)' : 'var(--color-info)',
                  border: '1px solid', borderColor: it.isRead ? 'var(--divider)' : 'var(--color-info)',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ ...ONE_LINE, fontWeight: it.isRead ? 'var(--fw-medium)' : 'var(--fw-bold)', color: it.isRead ? 'var(--text-secondary)' : 'var(--text-primary)' }}
                >
                  {it.title}
                </Typography>
                {it.preview && (
                  <Typography variant="caption" title={it.preview} sx={{ ...ONE_LINE, color: 'var(--text-secondary)', mt: '2px' }}>
                    {it.preview}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', flexShrink: 0, alignSelf: 'flex-start' }}>
                {it.whenLabel}
              </Typography>
            </Row>
          ))}
        </Box>
      )}

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   10 · credits (card) e 11 · plan (card) — versões de GRADE.
   Os mesmos escopos em `error` sobem para a faixa de alarme (ver AlarmBand).
   ═══════════════════════════════════════════════════════════════════════════ */

const METER_ICON = { whatsapp_messages: WhatsApp };

function CreditsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const meters = d.meters || [];

  return (
    <CardShell tone={card.status} accent delay={delay}>
      <CardHead
        icon={<SmsRounded sx={{ fontSize: 22 }} />}
        tone={card.status}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {meters.map((m) => {
          const Icon = METER_ICON[m.key] || SmsRounded;
          const t = tint(m.status);
          return (
            <Box
              key={m.key}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                borderRadius: 'var(--radius-sm)', bgcolor: t.bg, border: '1px solid', borderColor: t.border,
              }}
            >
              <Icon sx={{ fontSize: 26, color: t.fg, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ ...ONE_LINE, color: t.sub, fontWeight: 'var(--fw-medium)' }}>
                  {m.name}
                </Typography>
                <Typography variant="h5" component="p" sx={{ ...HEAD, ...NUM, color: t.fg, lineHeight: 1.1, mt: '2px' }}>
                  {m.value}
                </Typography>
                <Typography variant="caption" sx={{ color: t.sub, mt: '2px' }}>{m.label}</Typography>
              </Box>
              {m.action && (
                <Button onClick={() => onAction(m.action)} disableElevation sx={{ ...CTA_BTN_SX, minHeight: 40, px: 1.75 }}>
                  {m.action.label}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>

      <CardActions actions={card.actions} onAction={onAction} />
    </CardShell>
  );
}

function PlanLimits({ limits }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {(limits || []).map((l) => {
        const t = tint(l.status);
        const full = l.percent >= 100;
        return (
          <Box key={l.key} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ ...ONE_LINE, flex: 1, fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)' }}>
                {l.name}
              </Typography>
              <Typography variant="caption" sx={{ ...NUM, color: t.fg, fontWeight: 'var(--fw-bold)', flexShrink: 0 }}>
                {l.label}
              </Typography>
              {full && <ToneChip tone="error" icon={<ErrorRounded sx={{ fontSize: 14 }} />} label="No limite" />}
            </Box>
            <Meter percent={l.percent} tone={l.status} label={`${l.name}: ${l.label}`} />
          </Box>
        );
      })}
    </Box>
  );
}

function PlanCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  return (
    <CardShell tone={card.status} accent delay={delay}>
      <CardHead
        icon={<WorkspacePremiumRounded sx={{ fontSize: 22 }} />}
        tone={card.status}
        title={card.title}
        subtitle={card.subtitle}
        card={card}
        onDismiss={onDismiss}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{d.name}</Typography>
        <ToneChip
          tone={d.status === 'success' ? 'success' : 'error'}
          icon={d.status === 'success' ? <CheckCircleRounded sx={{ fontSize: 14 }} /> : <ErrorRounded sx={{ fontSize: 14 }} />}
          label={d.statusLabel}
        />
        {d.renewsLabel && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{d.renewsLabel}</Typography>
        )}
      </Box>

      <PlanLimits limits={d.tightLimits} />

      <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
        {(card.actions || []).map((action, i) => (
          <Button
            key={`${action.label}-${i}`}
            onClick={() => onAction(action)}
            disableElevation
            endIcon={i === 0 ? <ArrowForwardRounded /> : undefined}
            sx={i === 0 ? { ...CTA_BTN_SX, minHeight: 40 } : QUIET_BTN_SX}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   12 · domainOffer — o CARD COMERCIAL. Família de cor do CTA, não da marca.
   ═══════════════════════════════════════════════════════════════════════════ */

const CTA_SOFT = `color-mix(in srgb, ${CTA_FILL} 12%, var(--surface))`;
const CTA_EDGE = `color-mix(in srgb, ${CTA_FILL} 34%, var(--surface))`;

function DomainOfferCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const suggestions = d.suggestions || [];

  return (
    <CardShell delay={delay} sx={{ borderColor: CTA_EDGE, '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: CTA_FILL } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--radius-sm)',
            bgcolor: CTA_SOFT, border: '1px solid', borderColor: CTA_EDGE, color: CTA_FILL_DARK,
            display: 'grid', placeItems: 'center',
          }}
        >
          <LanguageRounded sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="overline" sx={{ color: CTA_FILL_DARK, fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
            Domínio próprio
          </Typography>
          <Typography variant="h6" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.2, textWrap: 'pretty' }}>
            {card.title}
          </Typography>
          {card.subtitle && (
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, lineHeight: 1.45, textWrap: 'pretty' }}>
              {card.subtitle}
            </Typography>
          )}
        </Box>
        <DismissButton card={card} onDismiss={onDismiss} />
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'var(--radius-sm)', bgcolor: CTA_SOFT, border: '1px solid', borderColor: CTA_EDGE, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, textWrap: 'pretty' }}>
          {d.pitch}
        </Typography>

        {suggestions.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {suggestions.map((s) => (
              <Box
                key={s}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.625,
                  borderRadius: 999, bgcolor: 'var(--surface)', border: '1px solid', borderColor: CTA_EDGE,
                }}
              >
                <CheckCircleRounded sx={{ fontSize: 14, color: CTA_FILL }} />
                <Typography variant="body2" sx={{ color: CTA_FILL_DARK, fontWeight: 'var(--fw-bold)', overflowWrap: 'anywhere' }}>
                  {s}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {d.pageTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: 16, color: CTA_FILL }} />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              O endereço vai apontar para{' '}
              <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>{d.pageTitle}</Box>
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {(card.actions || []).map((action, i) => (
          <Button
            key={`${action.label}-${i}`}
            onClick={() => onAction(action)}
            disableElevation
            endIcon={i === 0 ? <ArrowForwardRounded /> : undefined}
            sx={i === 0 ? CTA_BTN_SX : QUIET_BTN_SX}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </CardShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   13 · tips e 14 · mobileApp — conteúdo de peso BAIXO: faixas horizontais no rodapé,
   não caixas com a mesma silhueta dos cards operacionais.
   ═══════════════════════════════════════════════════════════════════════════ */

function TipsCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)',
        p: { xs: 1.5, sm: 1.75 }, display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0,
        ...rise(delay),
      }}
    >
      <LightbulbRounded aria-hidden="true" sx={{ fontSize: 24, color: 'var(--tint-info-fg)', flexShrink: 0, mt: '2px' }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="overline" component="h2" sx={{ color: 'var(--tint-info-sub)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
          {card.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--tint-info-fg)', lineHeight: 1.5, mt: 0.25, textWrap: 'pretty' }}>
          {d.text}
        </Typography>
        {(card.actions || []).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {card.actions.map((action, i) => (
              <Button
                key={`${action.label}-${i}`}
                onClick={() => onAction(action)}
                endIcon={ACTION_ICON[action.type]}
                sx={{
                  ...LINK_BTN_SX, color: 'var(--tint-info-fg)',
                  '&:hover': { bgcolor: 'var(--surface)' },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
      <DismissButton card={card} onDismiss={onDismiss} />
    </Box>
  );
}

/* O QR não mora aberto na faixa: 120px de código dariam a este card — `low` e dispensável,
   presente em TODA situação — o peso visual de um card de conta. Então entra como MINIATURA
   REAL do próprio QR (não um ícone: a pessoa reconhece "tem QR aqui" sem legenda) que amplia
   para o tamanho que uma câmera lê de fato. No celular ele desaparece — quem está no celular
   já está no aparelho que instalaria; ali as `actions` do card são o caminho.
   Abrir/fechar é estado de INTERFACE: useState local, sem action nem emit novo no contrato.
   Moldura em --surface como o QR do PIX em Domínio; o SVG traz o branco e a área de silêncio
   dentro dele, então lê igual nos temas de superfície escura. */
function MobileAppCard({ card, onAction, onDismiss, delay }) {
  const d = card.data || {};
  const [qrOpen, setQrOpen] = useState(false);
  const qrAlt = d.qrCodeAlt || 'QR code para instalar o app';

  const qrFrame = {
    bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
    borderRadius: 'var(--radius-sm)', display: 'block',
  };

  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        bgcolor: 'var(--color-primary-soft)', border: '1px solid', borderColor: 'var(--color-primary-soft)',
        p: { xs: 1.5, sm: 1.75 }, display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0,
        ...rise(delay),
      }}
    >
      <PhoneIphoneRounded aria-hidden="true" sx={{ fontSize: 24, color: 'var(--color-primary-dark)', flexShrink: 0, mt: '2px' }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="overline" component="h2" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
          {card.title}
        </Typography>
        <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', mt: 0.25, textWrap: 'pretty' }}>
          {d.headline}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5, mt: 0.25, textWrap: 'pretty' }}>
          {d.body}
        </Typography>
        {(d.highlights || []).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1.5 }, mt: 0.75 }}>
            {d.highlights.map((h) => (
              <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
                <CheckCircleRounded sx={{ fontSize: 14, color: 'var(--color-primary)', flexShrink: 0, mt: '2px' }} />
                <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', lineHeight: 1.4 }}>
                  {h}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {(card.actions || []).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {card.actions.map((action, i) => (
              <Button key={`${action.label}-${i}`} onClick={() => onAction(action)} endIcon={ACTION_ICON[action.type]} sx={LINK_BTN_SX}>
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {d.qrCodeUrl && (
        <Tooltip title="Ampliar para ler com a câmera">
          <ButtonBase
            onClick={() => setQrOpen(true)}
            aria-label="Ampliar o QR code de instalação do app"
            sx={{
              ...qrFrame, display: { xs: 'none', sm: 'block' },
              width: 62, height: 62, p: '3px', flexShrink: 0, alignSelf: 'center',
              transition: 'border-color .15s, box-shadow .15s',
              '&:hover': { borderColor: 'var(--color-primary)', boxShadow: 'var(--shadow-hover)' },
              '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
            }}
          >
            <Box component="img" src={d.qrCodeUrl} alt={qrAlt} sx={{ width: '100%', height: '100%', display: 'block' }} />
          </ButtonBase>
        </Tooltip>
      )}

      <DismissButton card={card} onDismiss={onDismiss} />

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        aria-labelledby="home-qr-title"
        PaperProps={{
          sx: {
            bgcolor: 'var(--surface)', borderRadius: 'var(--radius-lg)',
            border: '1px solid', borderColor: 'var(--divider)', backgroundImage: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
            <Typography id="home-qr-title" variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', flex: 1, textWrap: 'pretty' }}>
              {d.headline}
            </Typography>
            <IconButton aria-label="Fechar" onClick={() => setQrOpen(false)} sx={{ mt: '-6px', mr: '-6px', color: 'var(--text-secondary)', '&:hover': { bgcolor: 'var(--bg-page)' }, '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' } }}>
              <CloseRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Box sx={{ ...qrFrame, width: { xs: 232, sm: 264 }, height: { xs: 232, sm: 264 }, p: 1 }}>
            <Box component="img" src={d.qrCodeUrl} alt={qrAlt} sx={{ width: '100%', height: '100%', display: 'block' }} />
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5, textWrap: 'pretty' }}>
            Aponte a câmera do celular para o código.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   15 · features — vitrine de descoberta (só para quem está começando)
   ═══════════════════════════════════════════════════════════════════════════ */

const FEATURE_ICON = {
  publicBooking: EventAvailableRounded,
  pages: ArticleRounded,
  whatsapp: WhatsApp,
  finance: PaidRounded,
};

function FeaturesCard({ card, onAction, onDismiss, delay }) {
  const items = card.data?.items || [];
  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)',
        p: { xs: 1.25, sm: 1.75 }, display: 'flex', flexDirection: 'column', gap: 1.25,
        ...rise(delay),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeRounded aria-hidden="true" sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />
        <Typography variant="overline" component="h2" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
          {card.title}
        </Typography>
        <Box aria-hidden="true" sx={{ flex: 1, height: '1px', bgcolor: 'var(--divider)' }} />
        <DismissButton card={card} onDismiss={onDismiss} />
      </Box>

      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))' }}>
        {items.map((it) => {
          const Icon = FEATURE_ICON[it.key] || AutoAwesomeRounded;
          const body = (
            <>
              <Box
                aria-hidden="true"
                sx={{
                  width: 34, height: 34, borderRadius: '50%', mb: 1, flexShrink: 0,
                  bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
                  display: 'grid', placeItems: 'center',
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {it.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45, mt: 0.25, textWrap: 'pretty' }}>
                {it.description}
              </Typography>
              {it.action && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, mt: 0.75, color: 'var(--color-primary-dark)' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{it.action.label}</Typography>
                  <ChevronRightRounded sx={{ fontSize: 16 }} />
                </Box>
              )}
            </>
          );
          const sx = {
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%',
            minWidth: 0, p: 1.5, textAlign: 'left', borderRadius: 'var(--radius-sm)',
            bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
          };
          if (!it.action) return <Box key={it.key} sx={sx}>{body}</Box>;
          return (
            <ButtonBase
              key={it.key}
              focusRipple
              aria-label={`${it.title}: ${it.action.label}`}
              onClick={() => onAction(it.action)}
              sx={{
                ...sx, justifyContent: 'flex-start', transition: 'border-color .18s, box-shadow .18s, transform .18s',
                '&:hover': { borderColor: 'var(--color-primary)', boxShadow: 'var(--shadow-card)', transform: 'translateY(-2px)' },
                '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
                '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
              }}
            >
              {body}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   16 · FAIXA DE ALARME — o único momento em que a Home levanta a voz.
   Largura cheia, no topo, tom forte e botão de dinheiro. Nunca parece erro de sistema:
   tem rótulo do que aconteceu, o fato em números e um caminho de saída.
   ═══════════════════════════════════════════════════════════════════════════ */

const ALARM_META = {
  domainPayment: { eyebrow: 'Cobrança em aberto', Icon: ReceiptLongRounded },
  credits: { eyebrow: 'Envio interrompido', Icon: SmsFailedRounded },
  plan: { eyebrow: 'Limite do plano', Icon: WorkspacePremiumRounded },
};

function AlarmBand({ card, onAction, onDismiss, delay, children }) {
  const meta = ALARM_META[card.key] || { eyebrow: 'Precisa da sua atenção', Icon: WarningAmberRounded };
  const t = tint(card.status === 'neutral' ? 'warning' : card.status);
  const { Icon } = meta;

  return (
    <Box
      component="section"
      sx={{
        position: 'relative', overflow: 'hidden',
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        border: '1px solid', borderColor: t.border, bgcolor: t.bg,
        boxShadow: 'var(--shadow-card)',
        pl: { xs: 2, sm: 2.5 }, pr: { xs: 1.75, sm: 2.25 }, py: { xs: 1.75, sm: 2.25 },
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2.5 },
        ...rise(delay),
      }}
    >
      <Box aria-hidden="true" sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, bgcolor: t.main }} />

      <Box
        aria-hidden="true"
        sx={{
          width: 52, height: 52, flexShrink: 0, borderRadius: '50%',
          bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border, color: t.main,
          display: 'grid', placeItems: 'center', ...PULSE,
        }}
      >
        <Icon sx={{ fontSize: 26 }} />
      </Box>

      <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <WarningAmberRounded sx={{ fontSize: 15, color: t.sub }} />
            <Typography variant="overline" sx={{ color: t.sub, fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
              {meta.eyebrow}
            </Typography>
          </Box>
          <Typography variant="h6" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.2, mt: 0.25, textWrap: 'pretty' }}>
            {card.title}
          </Typography>
          {card.subtitle && (
            <Typography variant="body2" sx={{ color: t.fg, mt: 0.5, lineHeight: 1.5, textWrap: 'pretty' }}>
              {card.subtitle}
            </Typography>
          )}
        </Box>
        {children}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: { xs: 'stretch', sm: 'flex-end' }, flexShrink: 0 }}>
        {(card.actions || []).map((action, i) => (
          <Button
            key={`${action.label}-${i}`}
            onClick={() => onAction(action)}
            disableElevation
            endIcon={i === 0 ? <ArrowForwardRounded /> : undefined}
            sx={i === 0 ? { ...CTA_BTN_SX, minHeight: 48, px: 2.5 } : QUIET_BTN_SX}
          >
            {action.label}
          </Button>
        ))}
      </Box>

      <DismissButton card={card} onDismiss={onDismiss} />
    </Box>
  );
}

function DomainPaymentAlarm(props) {
  const d = props.card.data || {};
  const t = tint(props.card.status === 'neutral' ? 'warning' : props.card.status);
  return (
    <AlarmBand {...props}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.25, sm: 2.5 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: t.sub, fontWeight: 'var(--fw-medium)' }}>Domínio</Typography>
          <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', overflowWrap: 'anywhere', lineHeight: 1.3 }}>
            {d.domain}
          </Typography>
        </Box>
        <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', bgcolor: t.border, display: { xs: 'none', sm: 'block' } }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: t.sub, fontWeight: 'var(--fw-medium)' }}>Valor</Typography>
          <Typography variant="h6" component="p" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.2 }}>
            {d.priceLabel}
          </Typography>
        </Box>
        <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', bgcolor: t.border, display: { xs: 'none', sm: 'block' } }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <ToneChip tone={props.card.status} strong icon={<HourglassTopRounded sx={{ fontSize: 14, ...PULSE }} />} label={d.statusLabel} />
          <Typography variant="caption" sx={{ ...NUM, color: t.sub }}>desde {d.since}</Typography>
        </Box>
      </Box>
      {d.message && (
        <NoteRow tone="neutral" icon={<InfoRounded sx={{ fontSize: 18 }} />} sx={{ bgcolor: 'var(--surface)' }}>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.45, textWrap: 'pretty' }}>
            {d.message}
          </Typography>
        </NoteRow>
      )}
    </AlarmBand>
  );
}

function CreditsAlarm(props) {
  const meters = props.card.data?.meters || [];
  return (
    <AlarmBand {...props}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {meters.map((m) => {
          const Icon = METER_ICON[m.key] || SmsRounded;
          const t = tint(m.status);
          return (
            <Box
              key={m.key}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25, minWidth: 0,
                borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border,
              }}
            >
              <Icon sx={{ fontSize: 24, color: t.main, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ ...ONE_LINE, color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
                  {m.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="h5" component="p" sx={{ ...HEAD, ...NUM, color: t.fg, lineHeight: 1.1 }}>
                    {m.value}
                  </Typography>
                  <Typography variant="caption" sx={{ ...ONE_LINE, color: t.sub, fontWeight: 'var(--fw-medium)' }}>
                    {m.label}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </AlarmBand>
  );
}

function PlanAlarm(props) {
  const d = props.card.data || {};
  const t = tint(props.card.status === 'neutral' ? 'warning' : props.card.status);
  return (
    <AlarmBand {...props}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{d.name}</Typography>
        <ToneChip
          tone={d.status === 'success' ? 'success' : 'error'}
          icon={d.status === 'success' ? <CheckCircleRounded sx={{ fontSize: 14 }} /> : <ErrorRounded sx={{ fontSize: 14 }} />}
          label={d.statusLabel}
        />
        {d.renewsLabel && <Typography variant="caption" sx={{ color: t.sub }}>{d.renewsLabel}</Typography>}
      </Box>
      <Box
        sx={{
          p: 1.5, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)',
          border: '1px solid', borderColor: t.border,
          display: 'grid', gap: 1.5,
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
        }}
      >
        <PlanLimits limits={d.tightLimits} />
      </Box>
    </AlarmBand>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Registro de desenhos: uma entrada por `key`.
   ⚠️ Card novo aqui precisa entrar em CHAVES_COM_DESENHO (dashboardVmBuilder.js),
   senão o container omite a `key` e o desenho nunca aparece.
   ═══════════════════════════════════════════════════════════════════════════ */

const GRID_CARDS = {
  appointments: AppointmentsCard,
  absences: AbsencesCard,
  schedules: SchedulesCard,
  pages: PagesCard,
  firstSteps: FirstStepsCard,
  finance: FinanceCard,
  supportTickets: SupportTicketsCard,
  notifications: NotificationsCard,
  credits: CreditsCard,
  plan: PlanCard,
  domainOffer: DomainOfferCard,
};

const QUIET_CARDS = {
  registry: RegistryStrip,
  features: FeaturesCard,
  mobileApp: MobileAppCard,
  tips: TipsCard,
};

const ALARM_CARDS = {
  domainPayment: DomainPaymentAlarm,
  credits: CreditsAlarm,
  plan: PlanAlarm,
};

/* ═══════════════════════════════════════════════════════════════════════════
   A GRADE — largura preferida e largura máxima por `key`, em 12 colunas.
   `packSpans` fecha cada linha distribuindo a sobra: 7 ou 12 cards, sem buraco.
   ═══════════════════════════════════════════════════════════════════════════ */

const SPAN = {
  appointments: [8, 12],
  firstSteps: [8, 12],
  domainOffer: [8, 12],
  absences: [4, 12],
  schedules: [4, 12],
  pages: [4, 12],
  notifications: [4, 12],
  supportTickets: [4, 12],
  finance: [4, 12],
  credits: [4, 8],
  plan: [4, 8],
};

function packSpans(cards, cols) {
  const items = cards.map((c) => {
    const [pref, max] = SPAN[c.key] || [4, 12];
    const w = cols === 6 ? (pref >= 8 ? 6 : 3) : pref;
    const m = cols === 6 ? 6 : max;
    return { key: c.key, w: Math.min(w, cols), max: Math.min(Math.max(m, w), cols) };
  });

  const rows = [];
  let row = [];
  let used = 0;
  items.forEach((it) => {
    if (row.length && used + it.w > cols) { rows.push(row); row = []; used = 0; }
    row.push(it);
    used += it.w;
  });
  if (row.length) rows.push(row);

  rows.forEach((r) => {
    let free = cols - r.reduce((s, x) => s + x.w, 0);
    let guard = 0;
    while (free > 0 && guard < 24) {
      const before = free;
      r.forEach((x) => { if (free > 0 && x.w < x.max) { x.w += 1; free -= 1; } });
      if (free === before) break;
      guard += 1;
    }
    if (free > 0) r[r.length - 1].w += free;
  });

  const out = {};
  items.forEach((it) => { out[it.key] = it.w; });
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Estados da tela
   ═══════════════════════════════════════════════════════════════════════════ */

const SK = { bgcolor: 'var(--divider)' };

/** Skeleton com a FORMA do que vai chegar: herói com trilha + fila, e a grade em volta. */
function LoadingSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }} aria-busy="true" aria-live="polite">
      <Box sx={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: { xs: '1fr', sm: 'repeat(6, minmax(0, 1fr))', lg: 'repeat(12, minmax(0, 1fr))' } }}>
        <Box sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2.25 }, gridColumn: { xs: 'span 1', sm: 'span 6', lg: 'span 8' } }}>
          <Box sx={{ display: 'flex', gap: 1.25, mb: 2 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={148} sx={SK} />
              <Skeleton variant="text" width={96} sx={SK} />
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'minmax(200px, 264px) 1fr' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={64} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={`${52 + (i % 3) * 12}%`} sx={SK} />
                    <Skeleton variant="text" width="38%" sx={SK} />
                  </Box>
                  <Skeleton variant="rounded" width={88} height={20} sx={{ ...SK, borderRadius: 999 }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {[0, 1].map((i) => (
          <Box key={i} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2.25 }, gridColumn: { xs: 'span 1', sm: 'span 3', lg: i === 0 ? 'span 4' : 'span 4' } }}>
            <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
              <Skeleton variant="rounded" width={38} height={38} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" sx={SK} />
                <Skeleton variant="text" width="40%" sx={SK} />
              </Box>
            </Box>
            <Skeleton variant="text" width={72} height={30} sx={SK} />
            {[0, 1, 2].map((j) => (
              <Box key={j} sx={{ mt: 1.25 }}>
                <Skeleton variant="text" width={`${58 + j * 10}%`} sx={SK} />
                <Skeleton variant="text" width="34%" sx={SK} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={{ borderRadius: 'var(--radius-card)', border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)', p: 1.75 }}>
        <Skeleton variant="text" width={110} sx={{ ...SK, mb: 1 }} />
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function StateBlock({ icon, title, description, action }) {
  return (
    <Box
      sx={{
        ...CARD_SX, position: 'relative', overflow: 'hidden',
        px: 3, py: { xs: 5, sm: 7 },
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        ...rise(0),
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 72, height: 72, borderRadius: '50%', mb: 2,
          bgcolor: 'var(--color-primary-soft)', display: 'grid', placeItems: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.75, textWrap: 'pretty' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 520, lineHeight: 1.55, textWrap: 'pretty' }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Faixa acima de tudo. Não é card: não tem borda de card nem entra na grade. */
function Banner({ banner, onAction }) {
  const t = tint(banner.status);
  return (
    <Box
      component="section"
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.25, sm: 2 },
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-lg)' },
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-2)',
        ...rise(0),
      }}
    >
      <RocketLaunchRounded aria-hidden="true" sx={{ fontSize: 26, color: t.main, flexShrink: 0 }} />
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.25, textWrap: 'pretty' }}>
          {banner.title}
        </Typography>
        {banner.message && (
          <Typography variant="body2" sx={{ color: t.fg, mt: 0.25, lineHeight: 1.45, textWrap: 'pretty' }}>
            {banner.message}
          </Typography>
        )}
      </Box>
      {banner.action && (
        <Button onClick={() => onAction(banner.action)} endIcon={<ArrowForwardRounded />} disableElevation sx={PRIMARY_BTN_SX}>
          {banner.action.label}
        </Button>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

export default function DashboardView({ vm, actions }) {
  const a = actions || {};
  const onAction = (action) => { if (action) a.onAction?.(action); };
  const onDismiss = (key) => a.onDismiss?.(key);
  const [hoveredZone, setHoveredZone] = useState(false); // UI-only: realce do refresh no cabeçalho

  const greeting = vm.greeting || { title: '', subtitle: '' };
  const cards = vm.cards || [];

  // `critical` sempre grita; `credits`/`plan` em `error` também (serviço parado, teto batido).
  const isAlarm = (c) => !!ALARM_CARDS[c.key] && (c.importance === 'critical' || c.status === 'error');
  const alarms = cards.filter(isAlarm);
  const quiet = cards.filter((c) => !isAlarm(c) && QUIET_CARDS[c.key]);
  const grid = cards.filter((c) => !isAlarm(c) && GRID_CARDS[c.key]);

  const lg = packSpans(grid, 12);
  const sm = packSpans(grid, 6);

  const refreshButton = (
    <Tooltip title="Atualizar">
      <IconButton
        aria-label="Atualizar a página inicial"
        onClick={() => a.onRefresh?.()}
        sx={{
          width: 42, height: 42, color: 'var(--text-secondary)',
          border: '1px solid', borderColor: hoveredZone ? 'var(--color-primary-light)' : 'var(--divider)',
          bgcolor: 'var(--surface)',
          '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)', borderColor: 'var(--color-primary)' },
          '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
        }}
      >
        <RefreshRounded
          sx={{
            fontSize: 20,
            '@keyframes homeSpin': { to: { transform: 'rotate(360deg)' } },
            animation: vm.refreshing ? 'homeSpin 1s linear infinite' : 'none',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </IconButton>
    </Tooltip>
  );

  const header = (
    <Box
      onMouseEnter={() => setHoveredZone(true)}
      onMouseLeave={() => setHoveredZone(false)}
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 2, mb: 'var(--space-2)',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {vm.state === 'loading' ? (
          <>
            <Skeleton variant="text" width={220} height={44} sx={SK} />
            <Skeleton variant="text" width={168} sx={SK} />
          </>
        ) : (
          <>
            <Typography variant="h1" component="h1" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {greeting.title}
            </Typography>
            {greeting.subtitle && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.75, lineHeight: 1.45 }}>
                {greeting.subtitle}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box sx={{ textAlign: 'right', minWidth: 0 }} aria-live="polite">
          {vm.refreshing ? (
            <Typography variant="caption" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
              atualizando…
            </Typography>
          ) : vm.lastUpdatedLabel ? (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{vm.lastUpdatedLabel}</Typography>
          ) : null}
        </Box>
        {refreshButton}
      </Box>
    </Box>
  );

  let body = null;

  if (vm.state === 'loading') {
    body = <LoadingSkeleton />;
  } else if (vm.state === 'error') {
    body = (
      <StateBlock
        icon={<CloudOffRounded sx={{ fontSize: 34, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar sua página inicial"
        description={vm.errorMessage}
        action={(
          <Button onClick={() => a.onRefresh?.()} startIcon={<ReplayRounded />} sx={{ ...OUTLINE_BTN_SX, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (vm.isEmpty || cards.length === 0) {
    body = (
      <StateBlock
        icon={<RocketLaunchRounded sx={{ fontSize: 34, color: 'var(--color-primary-dark)' }} />}
        title="Sua página inicial começa a se preencher com o primeiro cadastro"
        description="Assim que existirem profissionais, serviços e uma agenda, é aqui que aparece o que precisa da sua atenção no dia: os próximos atendimentos, quem está fora, o que está no ar e qualquer cobrança em aberto."
        action={(
          <Button onClick={() => a.onRefresh?.()} startIcon={<ReplayRounded />} sx={OUTLINE_BTN_SX}>
            Conferir de novo
          </Button>
        )}
      />
    );
  } else {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {alarms.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {alarms.map((card, i) => {
              const Alarm = ALARM_CARDS[card.key];
              return <Alarm key={card.key} card={card} onAction={onAction} onDismiss={onDismiss} delay={i * 60} />;
            })}
          </Box>
        )}

        {grid.length > 0 && (
          <Box
            sx={{
              display: 'grid', gap: 'var(--space-2)', alignItems: 'stretch',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(6, minmax(0, 1fr))', lg: 'repeat(12, minmax(0, 1fr))' },
            }}
          >
            {grid.map((card, i) => {
              const Card = GRID_CARDS[card.key];
              return (
                <Box
                  key={card.key}
                  sx={{
                    minWidth: 0, display: 'flex',
                    gridColumn: { xs: 'span 1', sm: `span ${sm[card.key] || 3}`, lg: `span ${lg[card.key] || 4}` },
                    '& > section': { flex: 1 },
                  }}
                >
                  <Card card={card} onAction={onAction} onDismiss={onDismiss} delay={Math.min(i, 8) * 50} />
                </Box>
              );
            })}
          </Box>
        )}

        {quiet.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', mt: 'var(--space-1)' }}>
            {quiet.filter((c) => c.key === 'registry').map((card) => (
              <RegistryStrip key={card.key} card={card} onAction={onAction} onDismiss={onDismiss} delay={0} />
            ))}
            {(() => {
              const banners = quiet.filter((c) => c.key === 'mobileApp' || c.key === 'tips');
              if (!banners.length) return null;
              return (
                <Box sx={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))' }}>
                  {banners.map((card, i) => {
                    const Quiet = QUIET_CARDS[card.key];
                    return <Quiet key={card.key} card={card} onAction={onAction} onDismiss={onDismiss} delay={i * 50} />;
                  })}
                </Box>
              );
            })()}
            {quiet.filter((c) => c.key === 'features').map((card) => (
              <FeaturesCard key={card.key} card={card} onAction={onAction} onDismiss={onDismiss} delay={0} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ color: 'var(--text-primary)' }} aria-busy={vm.refreshing ? 'true' : undefined}>
      {header}

      {/* Refresh COM dados na tela: fio discreto, sem spinner de tela cheia e sem piscar. */}
      <Box sx={{ height: 3, mb: 'var(--space-2)', mt: '-8px' }}>
        {vm.refreshing && (
          <LinearProgress
            aria-label="Atualizando a página inicial"
            sx={{
              height: 3, borderRadius: 999, bgcolor: 'var(--divider)',
              '& .MuiLinearProgress-bar': { bgcolor: 'var(--color-primary)' },
            }}
          />
        )}
      </Box>

      {vm.banner && vm.state === 'ready' && <Banner banner={vm.banner} onAction={onAction} />}
      {body}
    </Box>
  );
}
